// Property owner lifecycle, mirroring the broker-db enum:
// - DRAFT: admin started creating but did not submit for review
// - PENDING_REVIEW: submitted for compliance review (default when an admin creates and submits)
// - ACTIVE: approved by compliance
// - SUSPENDED: system-imposed lockout
// - REJECTED: terminal; operator turned them away. rejectionReason is populated.
// - BLOCKED: admin-imposed (terminal until unblocked; blockReason populated)
export const PROPERTY_OWNER_STATUS = {
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending_review',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REJECTED: 'rejected',
  BLOCKED: 'blocked',
} as const

export const ENTITY_TYPE = {
  INDIVIDUAL: 'individual',
  COMPANY: 'company',
} as const

// Which intake door produced an owner record. Derived server-side from whether
// the owner has a live self-onboarding invite — an admin typing an owner in by
// hand never mints one.
//
// This is a view-level distinction, not a status: both sources share
// PROPERTY_OWNER_STATUS, the same review queues and the same activation gate.
// What differs is who is expected to fill the record in, and therefore who may
// safely edit it.
export const OWNER_ONBOARDING_SOURCE = {
  ADMIN: 'admin',
  SELF_SERVE: 'self_serve',
} as const

export type OwnerOnboardingSource =
  (typeof OWNER_ONBOARDING_SOURCE)[keyof typeof OWNER_ONBOARDING_SOURCE]

export const CTRL_ALT_STATUS = {
  NOT_SYNCED: 'not_synced',
  DRAFT: 'draft',
  PENDING: 'pending',
  ACTIVE: 'active',
  AWAITING_REGISTRATION: 'awaiting_registration',
  REJECTED: 'rejected',
} as const

export type PropertyOwnerStatus = (typeof PROPERTY_OWNER_STATUS)[keyof typeof PROPERTY_OWNER_STATUS]
export type EntityType = (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE]
export type CtrlAltStatus = (typeof CTRL_ALT_STATUS)[keyof typeof CTRL_ALT_STATUS]

// Keyed by status string value. PROPERTY_OWNER_STATUS and CTRL_ALT_STATUS
// share a few literals ('draft', 'active', 'rejected') — we list each
// string once and the color stays consistent across both axes.
export const STATUS_SEVERITY_MAP: Record<string, string> = {
  [PROPERTY_OWNER_STATUS.DRAFT]: 'info',
  [PROPERTY_OWNER_STATUS.PENDING_REVIEW]: 'info',
  [PROPERTY_OWNER_STATUS.ACTIVE]: 'success',
  [PROPERTY_OWNER_STATUS.SUSPENDED]: 'warning',
  [PROPERTY_OWNER_STATUS.REJECTED]: 'danger',
  [PROPERTY_OWNER_STATUS.BLOCKED]: 'danger',
  [CTRL_ALT_STATUS.NOT_SYNCED]: 'secondary',
  [CTRL_ALT_STATUS.PENDING]: 'warning',
  [CTRL_ALT_STATUS.AWAITING_REGISTRATION]: 'warning',
}

export const ENTITY_TYPE_OPTIONS = [ENTITY_TYPE.INDIVIDUAL, ENTITY_TYPE.COMPANY]
export const PROPERTY_OWNER_STATUS_OPTIONS = Object.values(PROPERTY_OWNER_STATUS)
